import React from 'react';
import { Loader } from '@mantine/core';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import InPlaceTextInput from 'components/form/in_place_text_input';
import XpAnimation from 'components/xp_animation';
import { SendIcon } from '@seedlang/icons';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { AppUI } from '@seedlang/state';
import Button from 'components/button/button';
import SpecialCharacterButtons from 'components/special_character_buttons';
import { CommentCreateWrapper } from '@seedlang/hoc';
import MarkdownLink from 'components/markdown_link';
import MarkdownSelectionToolbar from 'components/comment/markdown_selection_toolbar';
import FirstCommentModal from 'components/comment/first_comment_modal';
import RobotIcon from 'components/creator/robot_icon';
import {
  FORMAT_MARKERS,
  normalizeMarkdownUrl,
  selectedMarkdownFormatRange,
  selectedLinkRange,
  textareaCharacterPosition,
} from 'components/comment/comment_markdown_helpers';

const Wrapper = styled.div`
  margin: ${props => props.margin};
  width: ${props => props.width};
  background: ${props => props.background};
  text-align: left;
  .send-icon {
    svg {
      width: 14px;
      fill: #FFF;
      margin: 3px 4px 0 0;
    }
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CharacterLimit = styled.div`
  color: ${props => props.warning ? '#9F2A2A' : '#666'};
  font-size: 12px;
  line-height: 16px;
  margin: 6px 0 0 0;
`;

const Buttons = styled.div`
  display: flex;
  i {
    margin: 2px 3px 0 0;
  }
  .generate-icon {
    align-items: center;
    display: inline-flex;
    height: 17px;
    justify-content: center;
    margin-right: 6px;
    width: 17px;
  }
  button {
    margin: 10px 10px 0 0;
  }
`;

const Row = styled.div`
  display: flex;
  position: relative;
  @media only screen and (max-width : 649px) {
    flex-direction: column;
  }
`;

const Expand = styled.div`
  flex: 1;
  margin-right: 10px;
`;

const MarkdownLinkWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

@observer
class CommentCreate extends React.Component {
  @observable caretPosition;
  @observable selectionStart;
  @observable selectionEnd;
  @observable toolbarLeft = 0;
  @observable toolbarTop = 0;
  @observable toolbarVisible = false;
  selectionChangeFromKeyboard = false;
  textareaRef = React.createRef();
  toolbarPositionLocked = false;

  static defaultProps = {
    background: '#FFF',
    textInputButtonBackground: '#F1F1F1',
    placeholder: 'Leave a Comment',
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    window.removeEventListener('resize', this.onWindowResize);
  }

  insertText = (text) => {
    if (this.props.disabled) { return null; }
    const el = this.textarea;
    if (el) {
      const caretPosition = el.selectionStart;
      const newValue = [this.props.commentText.slice(0, caretPosition), text, this.props.commentText.slice(caretPosition)].join('');
      this.props.onChangeComment(newValue, caretPosition + 1);
    }
  }

  onDocumentMouseDown = (e) => {
    const textarea = this.textarea;
    const targetIsToolbar = e.target.closest && e.target.closest('[data-markdown-selection-toolbar]');
    if (e.target === textarea) {
      this.selectionChangeFromKeyboard = false;
      this.toolbarPositionLocked = false;
      return;
    }
    if (!this.toolbarVisible || targetIsToolbar) { return; }
    this.hideToolbar();
  }

  onTextareaSelect = (e) => {
    this.updateSelection(e.target);
  }

  onTextareaScroll = () => {
    if (!this.shouldRepositionToolbar) { return; }
    this.updateToolbarPosition();
  }

  onWindowResize = () => {
    if (!this.shouldRepositionToolbar) { return; }
    this.updateToolbarPosition();
  }

  onTextareaKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) {
      this.onKeyboardTextNavigation();
      return;
    }
    const key = e.key.toLowerCase();
    if (key === 'b') {
      e.preventDefault();
      this.toggleMarkdownFormat('bold', {keepToolbarVisible: this.toolbarVisible});
    } else if (key === 'i') {
      e.preventDefault();
      this.toggleMarkdownFormat('italic', {keepToolbarVisible: this.toolbarVisible});
    } else if (key === 's' && e.shiftKey) {
      e.preventDefault();
      this.toggleMarkdownFormat('strikethrough', {keepToolbarVisible: this.toolbarVisible});
    } else {
      this.onKeyboardTextNavigation();
    }
  }

  onKeyboardTextNavigation = () => {
    this.selectionChangeFromKeyboard = true;
    this.hideToolbar();
  }

  get textarea() {
    return this.textareaRef.current;
  }

  get shouldRepositionToolbar() {
    return this.toolbarVisible && !this.toolbarPositionLocked && !this.selectionChangeFromKeyboard;
  }

  get activeFormats() {
    const value = this.props.commentText || '';
    const start = this.selectionStart;
    const end = this.selectionEnd;

    if (!this.toolbarVisible || start === undefined || end === undefined || start === end) {
      return {};
    }

    return {
      bold: this.markdownFormatIsActive('bold', value, start, end),
      italic: this.markdownFormatIsActive('italic', value, start, end),
      link: Boolean(selectedLinkRange(value, start, end)),
      strikethrough: this.markdownFormatIsActive('strikethrough', value, start, end),
    };
  }

  hideToolbar = () => {
    this.toolbarPositionLocked = false;
    this.toolbarVisible = false;
  }

  updateSelection = (textarea) => {
    if (!textarea || this.props.disabled || AppUI.layout.isMobile) {
      this.hideToolbar();
      return;
    }

    this.selectionStart = textarea.selectionStart;
    this.selectionEnd = textarea.selectionEnd;

    if (this.selectionChangeFromKeyboard) {
      this.hideToolbar();
      return;
    }

    if (this.selectionStart === this.selectionEnd) {
      this.hideToolbar();
      return;
    }

    if (this.toolbarPositionLocked) {
      this.toolbarVisible = true;
      return;
    }

    this.updateToolbarPosition(textarea);
  }

  updateToolbarPosition = (textarea = this.textarea) => {
    if (AppUI.layout.isMobile || !textarea || this.selectionStart === undefined || this.selectionStart === this.selectionEnd) {
      return;
    }

    const startPosition = textareaCharacterPosition(textarea, this.selectionStart);
    const endPosition = textareaCharacterPosition(textarea, this.selectionEnd);
    const sameLine = Math.abs(startPosition.top - endPosition.top) < 24;
    this.toolbarLeft = sameLine ? (startPosition.left + endPosition.left) / 2 : startPosition.left;
    this.toolbarTop = startPosition.top - 8;
    this.toolbarVisible = true;
  }

  markdownFormatIsActive = (format, value, start, end) => {
    const marker = FORMAT_MARKERS[format];
    return Boolean(selectedMarkdownFormatRange(value, start, end, marker));
  }

  toggleMarkdownFormat = (format, options = {}) => {
    if (this.props.disabled) { return; }
    const textarea = this.textarea;
    const value = this.props.commentText || '';
    const marker = FORMAT_MARKERS[format];
    const start = textarea ? textarea.selectionStart : this.selectionStart;
    const end = textarea ? textarea.selectionEnd : this.selectionEnd;
    const selection = value.slice(start, end);
    const formatRange = selectedMarkdownFormatRange(value, start, end, marker);
    let newValue;
    let newSelectionStart;
    let newSelectionEnd;

    if (formatRange) {
      const formattedContent = value.slice(formatRange.contentStart, formatRange.contentEnd);
      newValue = `${value.slice(0, formatRange.markerStart)}${formattedContent}${value.slice(formatRange.markerEnd)}`;
      newSelectionStart = start <= formatRange.contentStart ? formatRange.markerStart : start - marker.length;
      newSelectionEnd = end >= formatRange.contentEnd ? formatRange.markerStart + formattedContent.length : end - marker.length;
    } else {
      newValue = `${value.slice(0, start)}${marker}${selection}${marker}${value.slice(end)}`;
      newSelectionStart = start + marker.length;
      newSelectionEnd = end + marker.length;
    }

    this.applyTextChange(newValue, newSelectionStart, newSelectionEnd, options);
  }

  onLink = () => {
    if (this.props.disabled) { return; }
    const value = this.props.commentText || '';
    const start = this.selectionStart;
    const end = this.selectionEnd;
    const selectedText = value.slice(start, end) || 'link text';
    const url = window.prompt('Enter URL');
    if (!url || !url.trim()) { return; }
    const normalizedUrl = normalizeMarkdownUrl(url.trim());
    if (!normalizedUrl) { return; }
    const linkText = `[${selectedText}](${normalizedUrl})`;
    const newValue = `${value.slice(0, start)}${linkText}${value.slice(end)}`;
    this.applyTextChange(newValue, start + 1, start + 1 + selectedText.length);
  }

  onUnlink = () => {
    if (this.props.disabled) { return; }
    const value = this.props.commentText || '';
    const linkRange = selectedLinkRange(value, this.selectionStart, this.selectionEnd);
    if (!linkRange) { return; }
    const newValue = `${value.slice(0, linkRange.fullStart)}${linkRange.label}${value.slice(linkRange.fullEnd)}`;
    this.applyTextChange(newValue, linkRange.fullStart, linkRange.fullStart + linkRange.label.length);
  }

  applyTextChange = (newValue, selectionStart, selectionEnd, options = {}) => {
    const keepToolbarVisible = options.keepToolbarVisible === undefined ? this.toolbarVisible : options.keepToolbarVisible;
    this.selectionStart = selectionStart;
    this.selectionEnd = selectionEnd;
    this.toolbarPositionLocked = keepToolbarVisible;
    this.props.onChangeComment(newValue, selectionEnd);
    window.requestAnimationFrame(() => {
      const textarea = this.textarea;
      if (!textarea) { return; }
      textarea.focus();
      textarea.setSelectionRange(selectionStart, selectionEnd);
      this.toolbarVisible = keepToolbarVisible && selectionStart !== selectionEnd;
    });
  }

  render() {
    return (
      <Wrapper
        margin={this.props.margin}
        width={this.props.width}
        background={this.props.background}
      >
        {
          this.props.showFirstCommentModal &&
            <FirstCommentModal
              title={this.props.firstCommentModalTitle}
              message={this.props.firstCommentModalMessage}
              confirmText={this.props.firstCommentConfirmText}
              cancelText={this.props.firstCommentCancelText}
              onConfirm={this.props.onConfirmFirstComment}
              onCancel={this.props.onCancelFirstComment}
            />
        }
        {
          this.props.showXp &&
            <XpAnimation
              namespace={this.props.namespace}
            />
        }
        <InputWrapper>
          <InPlaceTextInput
            height={this.props.height || '100px'}
            expandedHeight={this.props.blockTextExpansion ? (this.props.height || '100px') : '220px'}
            id={this.props.id}
            contractOnSubmit
            expandOnOverflow
            inputRef={this.textareaRef}
            inputType='textarea'
            placeholder={this.props.placeholder}
            onChange={this.props.onChangeComment}
            onSubmit={this.props.onSubmit}
            onKeyDown={this.onTextareaKeyDown}
            onScroll={this.onTextareaScroll}
            onSelect={this.onTextareaSelect}
            value={this.props.commentText}
            caretPosition={this.props.caretPosition}
            focusOnId={this.props.commentText}
            maxLength={this.props.maxLength}
          />
        </InputWrapper>
        <MarkdownSelectionToolbar
          activeFormats={this.activeFormats}
          left={this.toolbarLeft}
          onFormat={this.toggleMarkdownFormat}
          onLink={this.onLink}
          onUnlink={this.onUnlink}
          top={this.toolbarTop}
          visible={this.toolbarVisible && !AppUI.layout.isMobile}
        />
        {
          this.props.maxLength &&
          <CharacterLimit warning={(this.props.commentText || '').length >= this.props.maxLength}>
            {(this.props.commentText || '').length}/{this.props.maxLength} characters
          </CharacterLimit>
        }
        <Row>
          {
            !this.props.hideSpecialCharacters &&
              <Expand>
                <SpecialCharacterButtons
                  margin='10px 0 0 0'
                  languageId={this.props.languageId}
                  background={this.props.textInputButtonBackground}
                  onClick={this.insertText}
                />
              </Expand>
          }
          <MarkdownLinkWrapper>
            <MarkdownLink
              margin='5px 5px 0 0'
            />
          </MarkdownLinkWrapper>
        </Row>
        <Buttons>
          <Button
            onClick={this.props.onSubmit}
            disabled={this.props.disable}
            background={Theme.green}
          >
            <SendIcon />
            {this.props.edit ? 'Edit' : 'Submit'}
          </Button>
          {
            this.props.onGenerate &&
              <Button
                background={Theme.brightBlue}
                disabled={this.props.generating}
                onClick={this.props.onGenerate}
              >
                <span className='generate-icon'>
                  {
                    this.props.generating ?
                      <Loader color='white' size={12} /> :
                      <RobotIcon />
                  }
                </span>
                Generate
              </Button>
          }
        </Buttons>
      </Wrapper>
    );
  }
}

export default CommentCreateWrapper(CommentCreate);
